import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Bookmark, Share2, ChevronUp, Calendar, Clock, Eye, Facebook, Twitter, Linkedin, Link2 } from 'lucide-react';

export default function BlogArticleLayout({ darkMode = false, article, children }) {
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const bg          = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,15,26,0.55)';
  const cardBg      = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const cardBorder  = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const inputBg     = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const accent      = '#6366f1';
  const accent2     = '#8b5cf6';

  useEffect(() => {
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    return () => { document.documentElement.style.background = ''; document.body.style.background = ''; };
  }, [bg]);

  useEffect(() => {
    const handler = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setReadingProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleShare = async (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(article.title);
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter:  `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    if (platform === 'copy') {
      // On mobile use native share sheet (no iOS permission dialog).
      // Fall back to clipboard on desktop.
      if (navigator.share) {
        try {
          await navigator.share({ title: article.title, url: window.location.href });
        } catch (_) {}
      } else {
        try {
          await navigator.clipboard.writeText(window.location.href);
        } catch (_) {
          // execCommand fallback for older browsers
          const el = document.createElement('input');
          el.value = window.location.href;
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
        }
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      window.open(urls[platform], '_blank', 'noopener,noreferrer');
    }
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  const initials = (name) => name.split(' ').map(n => n[0]).join('');

  const iconBtn = (onClick, active, activeColor, children) => (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
      background: active ? activeColor + '15' : 'transparent',
      color: active ? activeColor : textSecondary,
      transition: 'background 0.2s, color 0.2s',
    }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >{children}</button>
  );

  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh' }}>
      {/* Reading progress */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 2, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', zIndex: 60 }}>
        <div style={{ height: '100%', width: `${readingProgress}%`, background: `linear-gradient(90deg, ${accent}, ${accent2})`, transition: 'width 0.1s' }} />
      </div>

      {/* Article nav bar */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: darkMode ? 'rgba(10,10,15,0.90)' : 'rgba(248,250,255,0.92)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${cardBorder}`,
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 54, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/blog')} style={{
            display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600, color: textSecondary, transition: 'color 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = accent}
            onMouseLeave={e => e.currentTarget.style.color = textSecondary}
          >
            <ArrowLeft size={15} /> Blog
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {iconBtn(() => setIsLiked(!isLiked), isLiked, '#ef4444', <><Heart size={14} style={{ fill: isLiked ? '#ef4444' : 'none' }} />{article.likes + (isLiked ? 1 : 0)}</>)}
            {iconBtn(() => setIsBookmarked(!isBookmarked), isBookmarked, accent, <><Bookmark size={14} style={{ fill: isBookmarked ? accent : 'none' }} /></>)}
            {iconBtn(() => handleShare('copy'), false, accent, <><Share2 size={14} />Teilen</>)}
          </div>
        </div>
      </nav>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 32 }} className="article-layout">
          <style>{`@media(min-width:1024px){.article-layout{grid-template-columns:200px 1fr!important}.article-sidebar{display:flex!important}}`}</style>

          {/* Sidebar */}
          <aside className="article-sidebar" style={{ display: 'none', flexDirection: 'column', gap: 16, alignSelf: 'start', position: 'sticky', top: 74 }}>

            {/* Table of Contents */}
            {article.tableOfContents?.length > 0 && (
              <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: '16px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: textSecondary, marginBottom: 10 }}>Inhalt</div>
                <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {article.tableOfContents.map(item => (
                    <a key={item.id} href={`#${item.id}`} style={{
                      display: 'block', padding: '5px 8px', borderRadius: 6, fontSize: 12, color: textSecondary,
                      textDecoration: 'none', transition: 'color 0.2s, background 0.2s', lineHeight: 1.4,
                    }}
                      onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = textSecondary; e.currentTarget.style.background = 'transparent'; }}
                    >{item.title}</a>
                  ))}
                </nav>
              </div>
            )}

            {/* Stats */}
            <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: '16px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: textSecondary, marginBottom: 10 }}>Statistiken</div>
              {[
                { Icon: Eye, label: 'Aufrufe', value: article.views },
                { Icon: Heart, label: 'Likes', value: article.likes + (isLiked ? 1 : 0) },
              ].map(({ Icon, label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: 12 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: textSecondary }}><Icon size={12} />{label}</span>
                  <span style={{ fontWeight: 700, color: textPrimary }}>{value}</span>
                </div>
              ))}
            </div>

            {/* Share */}
            <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: '16px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: textSecondary, marginBottom: 10 }}>Teilen</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { platform: 'facebook', Icon: Facebook, label: 'Facebook', color: '#1877f2' },
                  { platform: 'twitter',  Icon: Twitter,  label: 'X / Twitter', color: '#000' },
                  { platform: 'linkedin', Icon: Linkedin, label: 'LinkedIn', color: '#0a66c2' },
                  { platform: 'copy',     Icon: Link2,    label: copied ? 'Kopiert!' : 'Link kopieren', color: accent },
                ].map(({ platform, Icon, label, color }) => (
                  <button key={platform} onClick={() => handleShare(platform)} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, border: `1px solid ${cardBorder}`,
                    background: 'transparent', cursor: 'pointer', fontSize: 12, color: textSecondary, transition: 'border-color 0.2s, color 0.2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = color + '60'; e.currentTarget.style.color = color; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.color = textSecondary; }}
                  >
                    <Icon size={13} />{label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main article */}
          <main>
            {/* Article header */}
            <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 18, overflow: 'hidden', marginBottom: 32 }}>
              {/* Colored header band */}
              <div style={{ height: 6, background: `linear-gradient(90deg, ${accent}, ${accent2}, #a855f7)` }} />
              <div style={{ padding: '28px 28px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: accent2, background: accent2 + '15', padding: '3px 10px', borderRadius: 999 }}>{article.category}</span>
                  <span style={{ fontSize: 12, color: textSecondary, display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={11} />{article.readTime}</span>
                  <span style={{ fontSize: 12, color: textSecondary, display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={11} />{fmtDate(article.date)}</span>
                  <span style={{ fontSize: 12, color: textSecondary, display: 'flex', alignItems: 'center', gap: 4 }}><Eye size={11} />{article.views}</span>
                </div>
                <h1 style={{ fontSize: 'clamp(20px, 3vw, 36px)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.02em', marginBottom: 14 }}>{article.title}</h1>
                <p style={{ fontSize: 15, color: textSecondary, lineHeight: 1.65, marginBottom: 20 }}>{article.excerpt}</p>

                {/* Tags */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
                  {article.tags?.map(tag => (
                    <span key={tag} style={{ fontSize: 11, color: textSecondary, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', padding: '3px 9px', borderRadius: 6 }}>#{tag}</span>
                  ))}
                </div>

                {/* Author */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 16, borderTop: `1px solid ${cardBorder}`, paddingBottom: 20 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: `linear-gradient(135deg, ${accent}, ${accent2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {initials(article.author)}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary }}>{article.author}</div>
                    <div style={{ fontSize: 12, color: textSecondary, lineHeight: 1.4 }}>{article.authorBio}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Article body */}
            <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 18, padding: '28px 28px' }}>
              <style>{`
                .blog-prose h2{font-size:clamp(17px,2.2vw,24px);font-weight:800;line-height:1.25;letter-spacing:-0.01em;margin:32px 0 12px;color:${textPrimary}}
                .blog-prose h2:first-child{margin-top:0}
                .blog-prose h3{font-size:clamp(14px,1.6vw,18px);font-weight:700;margin:24px 0 8px;color:${textPrimary}}
                .blog-prose p{font-size:15px;line-height:1.75;color:${textSecondary};margin-bottom:14px}
                .blog-prose ul,.blog-prose ol{padding-left:20px;margin-bottom:14px}
                .blog-prose li{font-size:15px;line-height:1.7;color:${textSecondary};margin-bottom:6px}
                .blog-prose strong{color:${textPrimary};font-weight:700}
                .blog-prose a{color:${accent};text-decoration:none}
                .blog-prose a:hover{text-decoration:underline}
                .blog-prose .callout{background:${darkMode ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)'};border-left:3px solid ${accent};border-radius:0 10px 10px 0;padding:14px 18px;margin:18px 0}
                .blog-prose .callout h3{margin-top:0;font-size:14px}
                .blog-prose .callout p:last-child{margin-bottom:0}
                .blog-prose .highlight-box{background:${darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};border:1px solid ${cardBorder};border-radius:10px;padding:14px 18px;margin:18px 0}
                .blog-prose .highlight-box h4{font-size:13px;font-weight:700;margin-bottom:6px;color:${textPrimary}}
                .blog-prose .highlight-box p:last-child{margin-bottom:0}
                .blog-prose .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:18px 0}
                .blog-prose .grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:18px 0}
                @media(max-width:640px){.blog-prose .grid-2,.blog-prose .grid-3{grid-template-columns:1fr}}
                .blog-prose section{margin-bottom:0}
              `}</style>
              <div className="blog-prose">
                {children}
              </div>
            </div>

            {/* Newsletter */}
            <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 18, padding: '28px', marginTop: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 8 }}>Newsletter</div>
              <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Keinen Artikel verpassen</h3>
              <p style={{ fontSize: 13, color: textSecondary, marginBottom: 16 }}>Neue Beiträge direkt in dein Postfach — kostenlos.</p>
              <div style={{ display: 'flex', gap: 8, maxWidth: 380, margin: '0 auto' }}>
                <input type="email" placeholder="deine@email.de" style={{
                  flex: 1, padding: '10px 14px', borderRadius: 10, fontSize: 13,
                  background: inputBg, color: textPrimary, border: `1px solid ${cardBorder}`, outline: 'none', boxSizing: 'border-box',
                }}
                  onFocus={e => e.target.style.borderColor = accent}
                  onBlur={e => e.target.style.borderColor = cardBorder}
                />
                <button style={{
                  padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#fff', flexShrink: 0,
                  background: `linear-gradient(135deg, ${accent}, ${accent2})`,
                }}>
                  Anmelden
                </button>
              </div>
            </div>

            {/* Back link */}
            <div style={{ marginTop: 24 }}>
              <Link to="/blog" style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600,
                color: textSecondary, textDecoration: 'none', transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = accent}
                onMouseLeave={e => e.currentTarget.style.color = textSecondary}
              >
                <ArrowLeft size={14} /> Zurück zum Blog
              </Link>
            </div>
          </main>
        </div>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{
          position: 'fixed', bottom: 28, right: 28, width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: `linear-gradient(135deg, ${accent}, ${accent2})`, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 4px 14px ${accent}40`, zIndex: 50, transition: 'transform 0.2s',
        }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <ChevronUp size={18} />
        </button>
      )}
    </div>
  );
}
